import React, { useState, useMemo, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ComposedChart, Area, Line } from 'recharts';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import case5Data from '../data/MSP_Case5_optimized.json';
import { europeanCities } from '../data/casesData';
import { CONVERSION_CONSTANTS } from '../utils/conversionUtils';
import Case5ScenarioComparison from './Case5ScenarioComparison';

// Component to handle auto-zoom
const AutoZoomMap = ({ selections, regionCoordinates }) => {
  const map = useMap();

  useEffect(() => {
    const selectedCities = [
      selections.polysilicon,
      selections.ingotWafer,
      selections.cell,
      selections.module
    ].filter(city => city && regionCoordinates[city]);

    if (selectedCities.length > 0) {
      const bounds = L.latLngBounds(
        selectedCities.map(city => [
          regionCoordinates[city].lat,
          regionCoordinates[city].lng
        ])
      );
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 6 });
    }
  }, [selections, regionCoordinates, map]);

  return null;
};

// Region mapping for cities
const cityToRegion = {
  'Berlin': 'Eastern Germany',
  'Bilbao': 'Northern Spain',
  'Marseille': 'Southern France',
  'Moselle': 'Eastern France',
  'Padua': 'North-East Italy',
  'Stockholm': 'Central Sweden',
  'Sundsval': 'Northern Sweden',
  'Shanghai': 'Eastern China',
  'BacNinh': 'Northern Vietnam',
  'Brussels': 'Central Belgium'
};

// Regional center coordinates (approximate, less precise than city locations)
const regionCoordinates = {
  'Berlin': { lat: 52.5, lng: 13.0, region: 'Eastern Germany' },
  'Bilbao': { lat: 43.0, lng: -3.5, region: 'Northern Spain' },
  'Marseille': { lat: 43.5, lng: 5.0, region: 'Southern France' },
  'Moselle': { lat: 49.0, lng: 6.5, region: 'Eastern France' },
  'Padua': { lat: 45.5, lng: 11.5, region: 'North-East Italy' },
  'Stockholm': { lat: 59.5, lng: 17.5, region: 'Central Sweden' },
  'Sundsval': { lat: 63.0, lng: 17.0, region: 'Northern Sweden' },
  'Shanghai': { lat: 31.0, lng: 121.0, region: 'Eastern China' },
  'BacNinh': { lat: 21.5, lng: 106.0, region: 'Northern Vietnam' },
  'Brussels': { lat: 50.5, lng: 4.5, region: 'Central Belgium' }
};

const Case5Detailed = () => {
  const [activeTab, setActiveTab] = useState('analysis'); // 'analysis' or 'comparison'
  const [selections, setSelections] = useState({
    polysilicon: '',
    ingotWafer: '',
    cell: '',
    module: ''
  });
  const [chartType, setChartType] = useState('distribution'); // 'distribution' or 'breakdown'
  const [unit, setUnit] = useState('m2'); // 'm2', 'w', or 'eurw'

  // Color scheme (Becquerel Institute brand colors)
  const colors = {
    polysilicon: '#E78E42',
    ingotWafer: '#EAC144',
    cell: '#0A3C80',
    module: '#000755',
  };

  // Convert polysilicon from USD/kg to USD/m²
  const convertPolysiliconToM2 = (usdPerKg) => {
    return usdPerKg * CONVERSION_CONSTANTS.polysiliconConsumption; // 0.595 kg/m²
  };

  // Unit conversion functions
  const convertValue = (valueM2) => {
    if (unit === 'w') {
      return valueM2 / CONVERSION_CONSTANTS.powerPerM2;
    } else if (unit === 'eurw') {
      return (valueM2 / CONVERSION_CONSTANTS.powerPerM2) * CONVERSION_CONSTANTS.usdToEur;
    }
    return valueM2; // m2
  };

  const getUnitLabel = () => {
    if (unit === 'w') return '$/W';
    if (unit === 'eurw') return '€/W';
    return '$/m²';
  };

  const getUnitLabelLong = () => {
    if (unit === 'w') return 'USD per Watt';
    if (unit === 'eurw') return 'EUR per Watt';
    return 'USD per m²';
  };

  // Cost component labels and colors (ordered from tangible to abstract)
  const costLabels = {
    materials: 'Materials',                              // Most tangible - raw materials
    material_transportation: 'Material Transport',       // Physical logistics
    land: 'Land',                                        // Physical property
    equipment_depreciation: 'Equipment Depreciation',    // Physical machinery
    facility_depreciation: 'Facility Depreciation',      // Physical buildings
    electricity: 'Electricity',                          // Consumed utilities
    utilities: 'Utilities',                              // Gas, steam, water
    labour: 'Labour',                                    // Human resources
    sga: 'SG&A',                                        // Administrative overhead
    income: 'Income',                                    // Most abstract - profit/return
  };

  // Cost component colors - cohesive palette matching brand (ordered from tangible to abstract)
  const costColors = {
    'Materials': '#A0522D',              // Sienna brown (raw materials)
    'Material Transport': '#95A5A6',     // Gray (logistics)
    'Land': '#8B7355',                   // Tan/earth (property)
    'Equipment Depreciation': '#4A90E2', // Blue (machinery)
    'Facility Depreciation': '#7B68EE',  // Medium slate blue (buildings)
    'Electricity': '#FFD93D',            // Bright yellow (energy)
    'Utilities': '#6BCB77',              // Green (utilities)
    'Labour': '#E78E42',                 // Orange (brand color)
    'SG&A': '#9370DB',                   // Purple (administration)
    'Income': '#27AE60',                 // Green (profit)
  };

  // Extract available cities and create lookup map
  const { availableCities, lookupMap } = useMemo(() => {
    const cities = new Set();
    const map = {};

    Object.entries(case5Data).forEach(([id, data]) => {
      const locs = data.locations;
      cities.add(locs.polysilicon);
      cities.add(locs.ingot_wafer);
      cities.add(locs.cell);
      cities.add(locs.module);

      // Create lookup key
      const key = `${locs.polysilicon}|${locs.ingot_wafer}|${locs.cell}|${locs.module}`;
      map[key] = id;
    });

    return {
      availableCities: Array.from(cities).sort(),
      lookupMap: map
    };
  }, []);

  // Get selected combination data
  const selectedData = useMemo(() => {
    if (!selections.polysilicon || !selections.ingotWafer || !selections.cell || !selections.module) {
      return null;
    }

    const key = `${selections.polysilicon}|${selections.ingotWafer}|${selections.cell}|${selections.module}`;
    const comboId = lookupMap[key];
    return comboId ? case5Data[comboId] : null;
  }, [selections, lookupMap]);

  // Handle stage selection
  const handleStageChange = (stage, city) => {
    setSelections(prev => ({
      ...prev,
      [stage]: city
    }));
  };

  // Quick preset combinations
  const loadPreset = (preset) => {
    switch (preset) {
      case 'all-same':
        // Load first available city for all stages
        if (availableCities.length > 0) {
          const city = availableCities[2];
          setSelections({
            polysilicon: city,
            ingotWafer: city,
            cell: city,
            module: city
          });
        }
        break;
      case 'distributed':
        // Load distributed supply chain if available
        if (availableCities.length >= 4) {
          setSelections({
            polysilicon: availableCities[0],
            ingotWafer: availableCities[1],
            cell: availableCities[2],
            module: availableCities[3]
          });
        }
        break;
      case 'clear':
        setSelections({
          polysilicon: '',
          ingotWafer: '',
          cell: '',
          module: ''
        });
        break;
      default:
        break;
    }
  };

  // Prepare distribution chart data (histogram)
  const prepareDistributionData = () => {
    if (!selectedData || !selectedData.distributions || !selectedData.distributions.final_msp) return [];

    try {
      const mspValues = selectedData.distributions.final_msp.map(v => convertValue(v));
      if (!mspValues || mspValues.length === 0) return [];

      const min = Math.floor(Math.min(...mspValues) * 100) / 100;
      const max = Math.ceil(Math.max(...mspValues) * 100) / 100;
      const binCount = 20;
      const binSize = (max - min) / binCount;

      if (binSize === 0 || !isFinite(binSize)) return [];

      const decimals = unit === 'm2' ? 1 : 3;

      // Create bins
      const bins = Array.from({ length: binCount }, (_, i) => ({
        range: `${(min + i * binSize).toFixed(decimals)}-${(min + (i + 1) * binSize).toFixed(decimals)}`,
        midpoint: min + (i + 0.5) * binSize,
        count: 0
      }));

      // Fill bins
      mspValues.forEach(value => {
        if (isFinite(value)) {
          const binIndex = Math.min(Math.floor((value - min) / binSize), binCount - 1);
          if (binIndex >= 0 && binIndex < binCount) {
            bins[binIndex].count++;
          }
        }
      });

      return bins;
    } catch (error) {
      console.error('Error preparing distribution data:', error);
      return [];
    }
  };

  // Prepare cost breakdown data (stacked bar)
  const prepareCostBreakdownData = () => {
    if (!selectedData || !selectedData.cost_breakdown) return [];

    try {
      const stages = ['polysilicon', 'ingot_wafer', 'cell', 'module'];

      // Define the order explicitly (tangible to abstract)
      const orderedKeys = [
        'materials',
        'material_transportation',
        'land',
        'equipment_depreciation',
        'facility_depreciation',
        'electricity',
        'utilities',
        'labour',
        'sga',
        'income'
      ];

      return stages.map(stage => {
        const breakdown = selectedData.cost_breakdown[stage];
        if (!breakdown) return { stage: stage };

        const dataPoint = {
          stage: stage === 'ingot_wafer' ? 'Ingot/Wafer' :
                 stage.charAt(0).toUpperCase() + stage.slice(1)
        };

        // Add each cost component in the explicit order
        orderedKeys.forEach(key => {
          if (breakdown[key] && breakdown[key].mean !== undefined && costLabels[key]) {
            // Convert polysilicon costs from USD/kg to USD/m², then apply unit conversion
            const costValueM2 = stage === 'polysilicon'
              ? convertPolysiliconToM2(breakdown[key].mean)
              : breakdown[key].mean;
            dataPoint[costLabels[key]] = convertValue(costValueM2);
          }
        });

        return dataPoint;
      });
    } catch (error) {
      console.error('Error preparing cost breakdown data:', error);
      return [];
    }
  };

  // Prepare stage MSP comparison (incremental contributions) - Waterfall Chart
  const prepareStageMSPData = () => {
    if (!selectedData || !selectedData.statistics) return { stages: [], waterfallData: [] };

    try {
      // Get cumulative MSP values converted to $/m²
      const polysiliconMSP = convertPolysiliconToM2(selectedData.statistics.polysilicon_msp.mean);
      const ingotWaferMSP = selectedData.statistics.ingot_wafer_msp.mean;
      const cellMSP = selectedData.statistics.cell_msp.mean;
      const moduleMSP = selectedData.statistics.module_msp.mean;

      if (!isFinite(polysiliconMSP) || !isFinite(ingotWaferMSP) || !isFinite(cellMSP) || !isFinite(moduleMSP)) {
        return { stages: [], waterfallData: [] };
      }

      // Cell packing factor: only 94% of module area is covered by cells
      const CELL_PACKING_FACTOR = 0.94;
      const cellContributionToModule = cellMSP * CELL_PACKING_FACTOR;
      const packingAdjustment = cellMSP - cellContributionToModule; // 6% reduction

      // Calculate incremental contributions and apply unit conversion
      const stages = [
        {
          stage: 'Polysilicon',
          contribution: convertValue(polysiliconMSP),
          color: colors.polysilicon
        },
        {
          stage: 'Ingot/Wafer',
          contribution: convertValue(ingotWaferMSP - polysiliconMSP),
          color: colors.ingotWafer
        },
        {
          stage: 'Cell',
          contribution: convertValue(cellMSP - ingotWaferMSP),
          color: colors.cell
        },
        {
          stage: 'Packing Adjustment',
          contribution: convertValue(-packingAdjustment),
          color: '#FF6B6B',
          isAdjustment: true
        },
        {
          stage: 'Module',
          contribution: convertValue(moduleMSP - cellContributionToModule),
          color: colors.module
        }
      ];

      // Prepare waterfall chart data (each bar needs start position and value)
      let cumulative = 0;
      const waterfallData = [];

      stages.forEach((stage) => {
        waterfallData.push({
          name: stage.stage,
          base: cumulative,  // Starting position for this bar
          value: stage.contribution,  // The increment/decrement
          color: stage.color,
          isAdjustment: stage.isAdjustment || false
        });

        cumulative += stage.contribution;  // Update cumulative for next bar
      });

      return { stages, waterfallData };
    } catch (error) {
      console.error('Error preparing stage MSP data:', error);
      return { stages: [], waterfallData: [] };
    }
  };

  const distributionData = useMemo(() => prepareDistributionData(), [selectedData, unit]);
  const costBreakdownData = useMemo(() => prepareCostBreakdownData(), [selectedData, unit]);
  const stageMSPData = useMemo(() => prepareStageMSPData(), [selectedData, unit]);

  // Custom tooltip for distribution
  const DistributionTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const currencySymbol = unit === 'eurw' ? '€' : '$';
      return (
        <div className="bg-white p-3 border-2 rounded-lg shadow-lg" style={{ borderColor: '#E78E42' }}>
          <p className="font-semibold">MSP Range: {currencySymbol}{payload[0].payload.range}</p>
          <p className="text-gray-700">Frequency: {payload[0].value} iterations</p>
          <p className="text-gray-600 text-sm">({(payload[0].value).toFixed(1)}%)</p>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for cost breakdown
  const CostBreakdownTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
      const currencySymbol = unit === 'eurw' ? '€' : '$';
      const decimals = unit === 'm2' ? 2 : 3;
      return (
        <div className="bg-white p-3 border-2 rounded-lg shadow-lg" style={{ borderColor: '#E78E42' }}>
          <p className="font-semibold mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {currencySymbol}{entry.value.toFixed(decimals)}
            </p>
          ))}
          <p className="font-semibold mt-2 pt-2 border-t">Total: {currencySymbol}{total.toFixed(decimals)}</p>
        </div>
      );
    }
    return null;
  };

  // Custom Legend to control order (same approach as BuilderComparison)
  const CustomCostLegend = () => {
    const legendItems = [
      { name: 'Materials', color: costColors['Materials'] },
      { name: 'Material Transport', color: costColors['Material Transport'] },
      { name: 'Land', color: costColors['Land'] },
      { name: 'Equipment Depreciation', color: costColors['Equipment Depreciation'] },
      { name: 'Facility Depreciation', color: costColors['Facility Depreciation'] },
      { name: 'Electricity', color: costColors['Electricity'] },
      { name: 'Utilities', color: costColors['Utilities'] },
      { name: 'Labour', color: costColors['Labour'] },
      { name: 'SG&A', color: costColors['SG&A'] },
      { name: 'Income', color: costColors['Income'] },
    ];

    return (
      <div className="flex flex-wrap justify-center gap-3 pt-4">
        {legendItems.map((item) => (
          <div key={item.name} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-xs text-gray-700">{item.name}</span>
          </div>
        ))}
      </div>
    );
  };

  // Custom Legend for stage contributions (waterfall)
  const CustomStageLegend = () => {
    const legendItems = [
      { name: 'Polysilicon', color: colors.polysilicon, isPattern: false },
      { name: 'Ingot/Wafer', color: colors.ingotWafer, isPattern: false },
      { name: 'Cell', color: colors.cell, isPattern: false },
      { name: 'Packing Adjustment', color: '#FF6B6B', description: '-6% (94% cell coverage)', isPattern: true },
      { name: 'Module', color: colors.module, isPattern: false }
    ];

    return (
      <div className="flex flex-wrap justify-center gap-4 pt-4">
        {legendItems.map((item) => (
          <div key={item.name} className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded border"
              style={{
                backgroundColor: item.isPattern ? 'transparent' : item.color,
                backgroundImage: item.isPattern ? 'repeating-linear-gradient(45deg, #FF6B6B, #FF6B6B 2px, transparent 2px, transparent 6px)' : 'none',
                borderColor: item.isPattern ? '#FF6B6B' : 'transparent'
              }}
            />
            <span className="text-sm text-gray-700">
              {item.name}
              {item.description && <span className="text-xs text-gray-500 ml-1">{item.description}</span>}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-6 rounded-xl shadow-xl border border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-2 h-8 rounded-full" style={{ backgroundImage: 'linear-gradient(to bottom, #E78E42, #EAC144)' }}></div>
          <h2 className="text-2xl font-bold" style={{ color: '#000755' }}>
            Made in EU: Detailed Cost Analysis
          </h2>
        </div>
        <p className="text-gray-600 mb-4">
          Build your supply chain by selecting a location for each manufacturing stage. Explore detailed cost breakdowns and MSP distributions based on 1,000 Monte Carlo iterations.
        </p>

        {/* Tabs */}
        <div className="flex gap-2 border-b-2" style={{ borderColor: '#E78E42' }}>
          <button
            onClick={() => setActiveTab('analysis')}
            className={`px-6 py-3 font-semibold text-sm transition-all border-b-4 ${
              activeTab === 'analysis'
                ? 'text-white'
                : 'border-transparent hover:bg-gray-50'
            }`}
            style={
              activeTab === 'analysis'
                ? {
                    backgroundImage: 'linear-gradient(to right, #E78E42, #EAC144)',
                    borderColor: '#000755',
                    marginBottom: '-2px'
                  }
                : { color: '#0A3C80' }
            }
          >
            Cost Analysis
          </button>
          <button
            onClick={() => setActiveTab('comparison')}
            className={`px-6 py-3 font-semibold text-sm transition-all border-b-4 ${
              activeTab === 'comparison'
                ? 'text-white'
                : 'border-transparent hover:bg-gray-50'
            }`}
            style={
              activeTab === 'comparison'
                ? {
                    backgroundImage: 'linear-gradient(to right, #E78E42, #EAC144)',
                    borderColor: '#000755',
                    marginBottom: '-2px'
                  }
                : { color: '#0A3C80' }
            }
          >
            Scenario Comparison
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'analysis' ? (
        <>
          {/* Original Case 5 Detailed Analysis */}
          <div className="bg-white p-6 rounded-xl shadow-xl border border-gray-200">
            {/* Stage Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Polysilicon */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: colors.polysilicon }}>
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: colors.polysilicon }}></span>
                Polysilicon
              </span>
            </label>
            <select
              value={selections.polysilicon}
              onChange={(e) => handleStageChange('polysilicon', e.target.value)}
              className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-orange-400"
              style={{ borderColor: colors.polysilicon }}
            >
              <option value="">-- Select Location --</option>
              {availableCities.map(city => (
                <option key={city} value={city}>{cityToRegion[city] || city}</option>
              ))}
            </select>
          </div>

          {/* Ingot/Wafer */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: colors.ingotWafer }}>
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: colors.ingotWafer }}></span>
                Ingot/Wafer
              </span>
            </label>
            <select
              value={selections.ingotWafer}
              onChange={(e) => handleStageChange('ingotWafer', e.target.value)}
              className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-orange-400"
              style={{ borderColor: colors.ingotWafer }}
            >
              <option value="">-- Select Location --</option>
              {availableCities.map(city => (
                <option key={city} value={city}>{cityToRegion[city] || city}</option>
              ))}
            </select>
          </div>

          {/* Cell */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: colors.cell }}>
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: colors.cell }}></span>
                Cell
              </span>
            </label>
            <select
              value={selections.cell}
              onChange={(e) => handleStageChange('cell', e.target.value)}
              className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-orange-400"
              style={{ borderColor: colors.cell }}
            >
              <option value="">-- Select Location --</option>
              {availableCities.map(city => (
                <option key={city} value={city}>{cityToRegion[city] || city}</option>
              ))}
            </select>
          </div>

          {/* Module */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: colors.module }}>
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: colors.module }}></span>
                Module
              </span>
            </label>
            <select
              value={selections.module}
              onChange={(e) => handleStageChange('module', e.target.value)}
              className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-orange-400"
              style={{ borderColor: colors.module }}
            >
              <option value="">-- Select Location --</option>
              {availableCities.map(city => (
                <option key={city} value={city}>{cityToRegion[city] || city}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Quick Preset Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => loadPreset('all-same')}
            className="px-3 py-1.5 text-sm rounded-lg border-2 hover:shadow-md transition-all"
            style={{ borderColor: '#E78E42', color: '#0A3C80' }}
          >
            Quick Start: Single Location
          </button>
          <button
            onClick={() => loadPreset('distributed')}
            className="px-3 py-1.5 text-sm rounded-lg border-2 hover:shadow-md transition-all"
            style={{ borderColor: '#EAC144', color: '#0A3C80' }}
          >
            Quick Start: Distributed Chain
          </button>
          <button
            onClick={() => loadPreset('clear')}
            className="px-3 py-1.5 text-sm rounded-lg border-2 hover:shadow-md transition-all text-gray-600"
            style={{ borderColor: '#d1d5db' }}
          >
            Clear Selection
          </button>
        </div>

        {/* Supply Chain Route Display */}
        {(selections.polysilicon || selections.ingotWafer || selections.cell || selections.module) && (
          <div className="mt-4 p-4 rounded-lg border" style={{ backgroundImage: 'linear-gradient(to right, rgba(231, 142, 66, 0.1), rgba(234, 193, 68, 0.1))', borderColor: '#E78E42' }}>
            <h4 className="font-semibold mb-2 text-sm" style={{ color: '#000755' }}>
              Supply Chain Route:
            </h4>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span style={{ color: colors.polysilicon, fontWeight: 'bold' }}>
                {selections.polysilicon ? (cityToRegion[selections.polysilicon] || selections.polysilicon) : '???'}
              </span>
              <span className="text-gray-400">→</span>
              <span style={{ color: colors.ingotWafer, fontWeight: 'bold' }}>
                {selections.ingotWafer ? (cityToRegion[selections.ingotWafer] || selections.ingotWafer) : '???'}
              </span>
              <span className="text-gray-400">→</span>
              <span style={{ color: colors.cell, fontWeight: 'bold' }}>
                {selections.cell ? (cityToRegion[selections.cell] || selections.cell) : '???'}
              </span>
              <span className="text-gray-400">→</span>
              <span style={{ color: colors.module, fontWeight: 'bold' }}>
                {selections.module ? (cityToRegion[selections.module] || selections.module) : '???'}
              </span>
            </div>
            {selectedData && (
              <p className="text-sm text-gray-600 mt-2">
                Mean MSP: <span className="font-bold" style={{ color: colors.module }}>
                  {unit === 'eurw' ? '€' : '$'}{convertValue(selectedData.statistics.final_msp.mean).toFixed(unit === 'm2' ? 2 : 3)} {getUnitLabel()}
                </span>
              </p>
            )}
            {!selectedData && selections.polysilicon && selections.ingotWafer && selections.cell && selections.module && (
              <p className="text-sm text-red-600 mt-2">
                ⚠ This combination is not available in the dataset
              </p>
            )}

            {/* Map Visualization */}
            {selections.polysilicon && selections.ingotWafer && selections.cell && selections.module && (
              <div className="mt-4">
                <h5 className="font-semibold mb-2 text-xs text-gray-700">Supply Chain Map:</h5>
                <div className="h-80 rounded-lg overflow-hidden border-2" style={{ borderColor: '#E78E42' }}>
                  <MapContainer
                    center={[50, 10]}
                    zoom={4}
                    minZoom={3}
                    maxZoom={6}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={false}
                    zoomControl={false}
                    attributionControl={false}
                  >
                    {/* Esri World Light Gray canvas - keyless, minimal light style (CARTO free tiles now require an API key) */}
                    <TileLayer
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                      attribution='Tiles &copy; <a href="https://www.esri.com/">Esri</a> &mdash; Esri, HERE, Garmin, &copy; OpenStreetMap contributors'
                    />

                    {/* Auto-zoom to fit all selected locations */}
                    <AutoZoomMap selections={selections} regionCoordinates={regionCoordinates} />

                    {/* Polylines connecting regions */}
                    {selections.polysilicon && selections.ingotWafer && regionCoordinates[selections.polysilicon] && regionCoordinates[selections.ingotWafer] && (
                      <Polyline
                        positions={[
                          [regionCoordinates[selections.polysilicon].lat, regionCoordinates[selections.polysilicon].lng],
                          [regionCoordinates[selections.ingotWafer].lat, regionCoordinates[selections.ingotWafer].lng]
                        ]}
                        color={colors.ingotWafer}
                        weight={4}
                        opacity={0.8}
                      />
                    )}
                    {selections.ingotWafer && selections.cell && regionCoordinates[selections.ingotWafer] && regionCoordinates[selections.cell] && (
                      <Polyline
                        positions={[
                          [regionCoordinates[selections.ingotWafer].lat, regionCoordinates[selections.ingotWafer].lng],
                          [regionCoordinates[selections.cell].lat, regionCoordinates[selections.cell].lng]
                        ]}
                        color={colors.cell}
                        weight={4}
                        opacity={0.8}
                      />
                    )}
                    {selections.cell && selections.module && regionCoordinates[selections.cell] && regionCoordinates[selections.module] && (
                      <Polyline
                        positions={[
                          [regionCoordinates[selections.cell].lat, regionCoordinates[selections.cell].lng],
                          [regionCoordinates[selections.module].lat, regionCoordinates[selections.module].lng]
                        ]}
                        color={colors.module}
                        weight={4}
                        opacity={0.8}
                      />
                    )}

                    {/* Markers for each stage - larger for better visibility */}
                    {selections.polysilicon && regionCoordinates[selections.polysilicon] && (
                      <Marker
                        position={[regionCoordinates[selections.polysilicon].lat, regionCoordinates[selections.polysilicon].lng]}
                        icon={L.divIcon({
                          className: 'custom-marker',
                          html: `<div style="background-color: ${colors.polysilicon}; width: 28px; height: 28px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4);"></div>`,
                          iconSize: [28, 28],
                          iconAnchor: [14, 14]
                        })}
                      />
                    )}
                    {selections.ingotWafer && regionCoordinates[selections.ingotWafer] && (
                      <Marker
                        position={[regionCoordinates[selections.ingotWafer].lat, regionCoordinates[selections.ingotWafer].lng]}
                        icon={L.divIcon({
                          className: 'custom-marker',
                          html: `<div style="background-color: ${colors.ingotWafer}; width: 28px; height: 28px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4);"></div>`,
                          iconSize: [28, 28],
                          iconAnchor: [14, 14]
                        })}
                      />
                    )}
                    {selections.cell && regionCoordinates[selections.cell] && (
                      <Marker
                        position={[regionCoordinates[selections.cell].lat, regionCoordinates[selections.cell].lng]}
                        icon={L.divIcon({
                          className: 'custom-marker',
                          html: `<div style="background-color: ${colors.cell}; width: 28px; height: 28px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4);"></div>`,
                          iconSize: [28, 28],
                          iconAnchor: [14, 14]
                        })}
                      />
                    )}
                    {selections.module && regionCoordinates[selections.module] && (
                      <Marker
                        position={[regionCoordinates[selections.module].lat, regionCoordinates[selections.module].lng]}
                        icon={L.divIcon({
                          className: 'custom-marker',
                          html: `<div style="background-color: ${colors.module}; width: 28px; height: 28px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4);"></div>`,
                          iconSize: [28, 28],
                          iconAnchor: [14, 14]
                        })}
                      />
                    )}
                  </MapContainer>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Illustrative map showing approximate regional locations. Markers and connecting lines represent the supply chain flow. Map automatically zooms to show all selected locations.
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Map tiles &copy; <a href="https://www.esri.com/" target="_blank" rel="noopener noreferrer" className="underline">Esri</a> (Esri, HERE, Garmin) and <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" className="underline">OpenStreetMap</a> contributors.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Results Section */}
      {selectedData && (
        <>
          {/* Statistics Summary */}
          <div className="bg-white p-6 rounded-xl shadow-xl border border-gray-200">
            <h3 className="text-xl font-bold mb-4" style={{ color: '#000755' }}>
              MSP Statistics ({getUnitLabelLong()})
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(231, 142, 66, 0.1)' }}>
                <p className="text-sm text-gray-600 mb-1">Mean</p>
                <p className="text-2xl font-bold" style={{ color: '#E78E42' }}>
                  {unit === 'eurw' ? '€' : '$'}{convertValue(selectedData.statistics.final_msp.mean).toFixed(unit === 'm2' ? 2 : 3)}
                </p>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(234, 193, 68, 0.1)' }}>
                <p className="text-sm text-gray-600 mb-1">Median</p>
                <p className="text-2xl font-bold" style={{ color: '#EAC144' }}>
                  {unit === 'eurw' ? '€' : '$'}{convertValue(selectedData.statistics.final_msp.median).toFixed(unit === 'm2' ? 2 : 3)}
                </p>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(10, 60, 128, 0.1)' }}>
                <p className="text-sm text-gray-600 mb-1">Min</p>
                <p className="text-2xl font-bold" style={{ color: '#0A3C80' }}>
                  {unit === 'eurw' ? '€' : '$'}{convertValue(selectedData.statistics.final_msp.min).toFixed(unit === 'm2' ? 2 : 3)}
                </p>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(0, 7, 85, 0.1)' }}>
                <p className="text-sm text-gray-600 mb-1">Max</p>
                <p className="text-2xl font-bold" style={{ color: '#000755' }}>
                  {unit === 'eurw' ? '€' : '$'}{convertValue(selectedData.statistics.final_msp.max).toFixed(unit === 'm2' ? 2 : 3)}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-gray-100">
                <p className="text-sm text-gray-600 mb-1">Std Dev</p>
                <p className="text-2xl font-bold text-gray-700">
                  {unit === 'eurw' ? '€' : '$'}{convertValue(selectedData.statistics.final_msp.std_dev).toFixed(unit === 'm2' ? 2 : 3)}
                </p>
              </div>
            </div>
          </div>

          {/* Chart Toggle and Unit Selector */}
          <div className="bg-white p-4 rounded-xl shadow-xl border border-gray-200">
            <div className="flex flex-col md:flex-row gap-3">
              {/* Chart Type Toggle */}
              <div className="flex gap-3 flex-1">
                <button
                  onClick={() => setChartType('distribution')}
                  className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
                    chartType === 'distribution' ? 'text-white shadow-md' : 'text-gray-600 hover:text-gray-800'
                  }`}
                  style={chartType === 'distribution' ? { backgroundImage: 'linear-gradient(to right, #E78E42, #EAC144)' } : { backgroundColor: '#f3f4f6' }}
                >
                  MSP Distribution
                </button>
                <button
                  onClick={() => setChartType('breakdown')}
                  className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
                    chartType === 'breakdown' ? 'text-white shadow-md' : 'text-gray-600 hover:text-gray-800'
                  }`}
                  style={chartType === 'breakdown' ? { backgroundImage: 'linear-gradient(to right, #E78E42, #EAC144)' } : { backgroundColor: '#f3f4f6' }}
                >
                  Cost Breakdown
                </button>
              </div>

              {/* Unit Selector */}
              <div className="flex gap-2">
                <button
                  onClick={() => setUnit('m2')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    unit === 'm2' ? 'text-white shadow-md' : 'text-gray-600 hover:text-gray-800'
                  }`}
                  style={unit === 'm2' ? { backgroundColor: '#0A3C80' } : { backgroundColor: '#f3f4f6' }}
                >
                  $/m²
                </button>
                <button
                  onClick={() => setUnit('w')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    unit === 'w' ? 'text-white shadow-md' : 'text-gray-600 hover:text-gray-800'
                  }`}
                  style={unit === 'w' ? { backgroundColor: '#0A3C80' } : { backgroundColor: '#f3f4f6' }}
                >
                  $/W
                </button>
                <button
                  onClick={() => setUnit('eurw')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    unit === 'eurw' ? 'text-white shadow-md' : 'text-gray-600 hover:text-gray-800'
                  }`}
                  style={unit === 'eurw' ? { backgroundColor: '#0A3C80' } : { backgroundColor: '#f3f4f6' }}
                >
                  €/W
                </button>
              </div>
            </div>
          </div>

          {/* Charts */}
          {chartType === 'distribution' ? (
            <div className="bg-white p-6 rounded-xl shadow-xl border border-gray-200">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#000755' }}>
                MSP Distribution (1,000 Iterations)
              </h3>
              {distributionData && distributionData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={distributionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="midpoint"
                      label={{ value: `MSP (${getUnitLabel()})`, position: 'insideBottom', offset: -5 }}
                      tickFormatter={(value) => value.toFixed(unit === 'm2' ? 0 : 2)}
                    />
                    <YAxis label={{ value: 'Frequency', angle: -90, position: 'insideLeft' }} />
                    <Tooltip content={<DistributionTooltip />} />
                    <Bar dataKey="count" fill={colors.polysilicon} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-96 flex items-center justify-center text-gray-500">
                  <p>Loading distribution data...</p>
                </div>
              )}

              {/* Stage MSP Comparison */}
              <h3 className="text-xl font-bold mb-4 mt-8" style={{ color: '#000755' }}>
                Incremental Stage Contributions (Waterfall Chart)
              </h3>
              <p className="text-sm text-gray-600 mb-2">
                Each bar shows the incremental cost change at each stage ({getUnitLabel()}). Bars build on previous stages.
              </p>
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  <span className="font-semibold">ℹ️ Cell Packing Factor:</span> Cells only cover 94% of module area.
                  The red striped "Packing Adjustment" (-6%) shows the reduction when converting from cell area to module area.
                  Each bar starts where the previous one ended, creating a cascading waterfall effect.
                </p>
              </div>

              {/* Summary of contributions */}
              {stageMSPData && stageMSPData.stages && stageMSPData.stages.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                {stageMSPData.stages.map((stage) => (
                  <div
                    key={stage.stage}
                    className={`p-3 rounded-lg border-2 ${stage.isAdjustment ? 'relative' : ''}`}
                    style={{
                      borderColor: stage.color,
                      backgroundColor: stage.isAdjustment ? '#FEF2F2' : `${stage.color}10`,
                      backgroundImage: stage.isAdjustment ? 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,107,107,0.1) 10px, rgba(255,107,107,0.1) 20px)' : 'none'
                    }}
                  >
                    <p className="text-xs text-gray-600 mb-1">{stage.stage}</p>
                    <p className="text-lg font-bold" style={{ color: stage.color }}>
                      {stage.contribution >= 0 ? (unit === 'eurw' ? '€' : '$') : (unit === 'eurw' ? '-€' : '-$')}
                      {Math.abs(stage.contribution).toFixed(unit === 'm2' ? 2 : 3)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {((Math.abs(stage.contribution) / convertValue(selectedData.statistics.final_msp.mean)) * 100).toFixed(1)}%
                    </p>
                  </div>
                ))}
              </div>
              )}

              {stageMSPData && stageMSPData.waterfallData && stageMSPData.waterfallData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={stageMSPData.waterfallData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                  <defs>
                    <pattern id="packingPattern" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                      <line x1="0" y1="0" x2="0" y2="8" stroke="#FF6B6B" strokeWidth="3" />
                    </pattern>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-15}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis
                    label={{ value: `MSP (${getUnitLabel()})`, angle: -90, position: 'insideLeft' }}
                    domain={[0, 'auto']}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        const startValue = data.base;
                        const endValue = data.base + data.value;
                        const currencySymbol = unit === 'eurw' ? '€' : '$';
                        const decimals = unit === 'm2' ? 2 : 3;
                        return (
                          <div className="bg-white p-3 border-2 rounded-lg shadow-lg" style={{ borderColor: '#E78E42' }}>
                            <p className="font-semibold mb-2">{data.name}</p>
                            <p className="text-gray-700">
                              {data.value >= 0 ? 'Increase' : 'Decrease'}: {data.value >= 0 ? currencySymbol : `-${currencySymbol}`}{Math.abs(data.value).toFixed(decimals)}
                            </p>
                            <p className="text-gray-600 text-sm">From: {currencySymbol}{startValue.toFixed(decimals)}</p>
                            <p className="text-gray-600 text-sm">To: {currencySymbol}{endValue.toFixed(decimals)}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {/* Invisible base bars to position each bar at the correct height */}
                  <Bar dataKey="base" stackId="a" fill="transparent" />
                  {/* Visible bars showing the increments/decrements */}
                  <Bar dataKey="value" stackId="a">
                    {stageMSPData.waterfallData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          entry.isAdjustment
                            ? 'url(#packingPattern)'
                            : entry.color
                        }
                        stroke={entry.isAdjustment ? '#FF6B6B' : 'none'}
                        strokeWidth={entry.isAdjustment ? 2 : 0}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              ) : (
                <div className="h-96 flex items-center justify-center text-gray-500">
                  <p>Loading waterfall chart data...</p>
                </div>
              )}

              {/* Custom Legend with correct order */}
              {stageMSPData && stageMSPData.stages && stageMSPData.stages.length > 0 && <CustomStageLegend />}
            </div>
          ) : (
            <div className="bg-white p-6 rounded-xl shadow-xl border border-gray-200">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#000755' }}>
                Cost Breakdown by Supply Chain Stage
              </h3>
              <p className="text-gray-600 mb-2 text-sm">
                Average cost components for each manufacturing stage ({getUnitLabel()}). Polysilicon converted from $/kg using {CONVERSION_CONSTANTS.polysiliconConsumption} kg/m² consumption rate.
              </p>
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  <span className="font-semibold">ℹ️ Note:</span> Cost values shown are per unit area of each stage (Cell costs are $/m² of cell area, Module costs are $/m² of module area).
                  Since cells cover only 94% of module area, cell MSP must be multiplied by 0.94 when calculating module MSP contribution.
                </p>
              </div>

              {/* Color Legend */}
              <div className="mb-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2">Cost Component Colors (Tangible → Abstract):</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                  {['Materials', 'Material Transport', 'Land', 'Equipment Depreciation', 'Facility Depreciation',
                    'Electricity', 'Utilities', 'Labour', 'SG&A', 'Income'].map((label) => (
                    <div key={label} className="flex items-center gap-1.5">
                      <span
                        className="w-3 h-3 rounded-sm flex-shrink-0"
                        style={{ backgroundColor: costColors[label] }}
                      ></span>
                      <span className="text-gray-600">{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <ResponsiveContainer width="100%" height={500}>
                <BarChart data={costBreakdownData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" label={{ value: `Cost (${getUnitLabel()})`, position: 'insideBottom', offset: -5 }} />
                  <YAxis type="category" dataKey="stage" width={100} />
                  <Tooltip content={<CostBreakdownTooltip />} />
                  {/* Bars ordered from tangible to abstract */}
                  <Bar dataKey="Materials" name="Materials" stackId="a" fill={costColors['Materials']} />
                  <Bar dataKey="Material Transport" name="Material Transport" stackId="a" fill={costColors['Material Transport']} />
                  <Bar dataKey="Land" name="Land" stackId="a" fill={costColors['Land']} />
                  <Bar dataKey="Equipment Depreciation" name="Equipment Depreciation" stackId="a" fill={costColors['Equipment Depreciation']} />
                  <Bar dataKey="Facility Depreciation" name="Facility Depreciation" stackId="a" fill={costColors['Facility Depreciation']} />
                  <Bar dataKey="Electricity" name="Electricity" stackId="a" fill={costColors['Electricity']} />
                  <Bar dataKey="Utilities" name="Utilities" stackId="a" fill={costColors['Utilities']} />
                  <Bar dataKey="Labour" name="Labour" stackId="a" fill={costColors['Labour']} />
                  <Bar dataKey="SG&A" name="SG&A" stackId="a" fill={costColors['SG&A']} />
                  <Bar dataKey="Income" name="Income" stackId="a" fill={costColors['Income']} />
                </BarChart>
              </ResponsiveContainer>

              {/* Custom Legend with correct order */}
              <CustomCostLegend />
            </div>
          )}

          {/* Detailed Cost Table */}
          <div className="bg-white p-6 rounded-xl shadow-xl border border-gray-200">
            <h3 className="text-xl font-bold mb-4" style={{ color: '#000755' }}>
              Detailed Cost Components ({getUnitLabel()})
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              All values shown in {getUnitLabel()}. Polysilicon costs converted from $/kg using consumption rate of {CONVERSION_CONSTANTS.polysiliconConsumption} kg/m².
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2" style={{ borderColor: '#E78E42' }}>
                    <th className="text-left py-3 px-4" style={{ color: '#0A3C80' }}>Cost Component</th>
                    <th className="text-right py-3 px-4" style={{ color: colors.polysilicon }}>Polysilicon</th>
                    <th className="text-right py-3 px-4" style={{ color: colors.ingotWafer }}>Ingot/Wafer</th>
                    <th className="text-right py-3 px-4" style={{ color: colors.cell }}>Cell</th>
                    <th className="text-right py-3 px-4" style={{ color: colors.module }}>Module</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { key: 'materials', label: 'Materials' },
                    { key: 'material_transportation', label: 'Material Transport' },
                    { key: 'land', label: 'Land' },
                    { key: 'equipment_depreciation', label: 'Equipment Depreciation' },
                    { key: 'facility_depreciation', label: 'Facility Depreciation' },
                    { key: 'electricity', label: 'Electricity' },
                    { key: 'utilities', label: 'Utilities' },
                    { key: 'labour', label: 'Labour' },
                    { key: 'sga', label: 'SG&A' },
                    { key: 'income', label: 'Income' },
                  ].map(({ key, label }) => (
                    <tr key={key} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-4 font-medium">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-sm flex-shrink-0"
                            style={{ backgroundColor: costColors[label] }}
                          ></span>
                          {label}
                        </div>
                      </td>
                      <td className="text-right py-2 px-4">
                        {unit === 'eurw' ? '€' : '$'}{convertValue(convertPolysiliconToM2(selectedData.cost_breakdown.polysilicon[key]?.mean || 0)).toFixed(unit === 'm2' ? 2 : 3)}
                      </td>
                      <td className="text-right py-2 px-4">
                        {unit === 'eurw' ? '€' : '$'}{convertValue(selectedData.cost_breakdown.ingot_wafer[key]?.mean || 0).toFixed(unit === 'm2' ? 2 : 3)}
                      </td>
                      <td className="text-right py-2 px-4">
                        {unit === 'eurw' ? '€' : '$'}{convertValue(selectedData.cost_breakdown.cell[key]?.mean || 0).toFixed(unit === 'm2' ? 2 : 3)}
                      </td>
                      <td className="text-right py-2 px-4">
                        {unit === 'eurw' ? '€' : '$'}{convertValue(selectedData.cost_breakdown.module[key]?.mean || 0).toFixed(unit === 'm2' ? 2 : 3)}
                      </td>
                    </tr>
                  ))}
                  <tr className="font-bold border-t-2" style={{ borderColor: '#0A3C80' }}>
                    <td className="py-3 px-4">Total MSP ({getUnitLabel()} of stage area)</td>
                    <td className="text-right py-3 px-4" style={{ color: colors.polysilicon }}>
                      {unit === 'eurw' ? '€' : '$'}{convertValue(convertPolysiliconToM2(selectedData.statistics.polysilicon_msp.mean)).toFixed(unit === 'm2' ? 2 : 3)}
                    </td>
                    <td className="text-right py-3 px-4" style={{ color: colors.ingotWafer }}>
                      {unit === 'eurw' ? '€' : '$'}{convertValue(selectedData.statistics.ingot_wafer_msp.mean).toFixed(unit === 'm2' ? 2 : 3)}
                    </td>
                    <td className="text-right py-3 px-4" style={{ color: colors.cell }}>
                      {unit === 'eurw' ? '€' : '$'}{convertValue(selectedData.statistics.cell_msp.mean).toFixed(unit === 'm2' ? 2 : 3)}
                    </td>
                    <td className="text-right py-3 px-4" style={{ color: colors.module }}>
                      {unit === 'eurw' ? '€' : '$'}{convertValue(selectedData.statistics.module_msp.mean).toFixed(unit === 'm2' ? 2 : 3)}
                    </td>
                  </tr>
                  <tr className="bg-blue-50 border-t italic text-sm">
                    <td className="py-2 px-4 text-gray-700">
                      <span className="font-semibold">Cell Packing Factor</span>
                      <div className="text-xs text-gray-600">Cells cover 94% of module area</div>
                    </td>
                    <td className="text-right py-2 px-4 text-gray-500">-</td>
                    <td className="text-right py-2 px-4 text-gray-500">-</td>
                    <td className="text-right py-2 px-4 text-gray-700">× 0.94</td>
                    <td className="text-right py-2 px-4 text-gray-500">-</td>
                  </tr>
                  <tr className="bg-blue-50 border-b-2" style={{ borderColor: '#0A3C80' }}>
                    <td className="py-2 px-4 text-gray-700">
                      <span className="font-semibold">Cell contribution to Module MSP</span>
                      <div className="text-xs text-gray-600">(Cell MSP converted to module area)</div>
                    </td>
                    <td className="text-right py-2 px-4 text-gray-500">-</td>
                    <td className="text-right py-2 px-4 text-gray-500">-</td>
                    <td className="text-right py-2 px-4 font-semibold text-blue-700">
                      {unit === 'eurw' ? '€' : '$'}{convertValue(selectedData.statistics.cell_msp.mean * 0.94).toFixed(unit === 'm2' ? 2 : 3)}
                    </td>
                    <td className="text-right py-2 px-4 text-gray-500">-</td>
                  </tr>
                  <tr className="bg-yellow-50 font-semibold">
                    <td className="py-3 px-4">
                      <span>Module increment (actual contribution)</span>
                      <div className="text-xs font-normal text-gray-600">Module MSP - Cell contribution to module</div>
                    </td>
                    <td className="text-right py-3 px-4 text-gray-500">-</td>
                    <td className="text-right py-3 px-4 text-gray-500">-</td>
                    <td className="text-right py-3 px-4 text-gray-500">-</td>
                    <td className="text-right py-3 px-4" style={{ color: colors.module }}>
                      {unit === 'eurw' ? '€' : '$'}{convertValue(selectedData.statistics.module_msp.mean - selectedData.statistics.cell_msp.mean * 0.94).toFixed(unit === 'm2' ? 2 : 3)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

          {/* No Selection State */}
          {!selectedData && (
            <div className="bg-white p-12 rounded-xl shadow-xl border border-gray-200 text-center">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className="text-xl text-gray-600">Build your supply chain to view detailed analysis</p>
              <p className="text-sm text-gray-500 mt-2">Select a location for each manufacturing stage above</p>
            </div>
          )}
        </>
      ) : (
        /* Scenario Comparison Tab */
        <div className="bg-white p-6 rounded-xl shadow-xl border border-gray-200">
          <Case5ScenarioComparison />
        </div>
      )}

      {/* SolarIntelligence.ai Promotional Section */}
      <div className="mt-8 rounded-xl p-6 shadow-lg" style={{ backgroundImage: 'linear-gradient(to right, #0A3C80, #000755)' }}>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          <div className="flex-shrink-0 bg-white rounded-lg p-4 shadow-lg">
            <img
              src="/new_logo.svg"
              alt="SolarIntelligence.ai"
              className="h-16 sm:h-20 w-auto"
            />
          </div>
          <div className="flex-1">
            <h4 className="text-xl font-bold mb-2 text-white">
              Advanced Solar Manufacturing Analytics
            </h4>
            <p className="text-sm text-blue-50 mb-4">
              <strong className="text-white">SolarIntelligence.ai</strong> provides advanced analytics for solar intelligence.
              Explore detailed cost modeling, supply chain optimization, and compliance assessment tools for your solar manufacturing scenarios.
            </p>
            <a
              href="https://solarintelligence.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold shadow-md hover:shadow-xl transition-all text-sm"
              style={{ backgroundImage: 'linear-gradient(to right, #E78E42, #EAC144)', color: 'white' }}
            >
              <span>Visit SolarIntelligence.ai</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Case5Detailed;
